import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2, XCircle, ShieldAlert, History, Eye, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const APPLY_POLL_INTERVAL_MS = 2000;

function defaultPriorMonth() {
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth(); // 0-indexed, so this is already "prior month"
  if (m === 0) { y -= 1; m = 12; }
  return { year: y, month: m };
}

/** Return true if the given year/month is a completed month (before current month). */
function isCompletedMonth(y, m) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  if (y < currentYear) return true;
  if (y === currentYear && m < currentMonth) return true;
  return false;
}

// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function AutoFinalizePanel(props) {
  const { entityId } = props;
  const { year: defaultYear, month: defaultMonth } = defaultPriorMonth();
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Row-level Preview/Apply: the drain front door for drafts the generator
  // already created in an earlier run (e.g. a July backlog) that were
  // never finalized. Preview is read-only; Apply enqueues a background
  // chain (POST returns 202 immediately -- the prior synchronous version
  // 504'd on a large candidate count) and this panel polls chain-status
  // for per-row progress until it completes.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyChain, setApplyChain] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const applyPollRef = useRef(null);

  // Read-only door onto sync_logs' per-invoice finalize_attempt entries --
  // lets the operator see, from the browser, how many invoices a
  // partially-completed run (e.g. a pre-fix 504) actually finalized before
  // it was cut off. This sandbox cannot query production directly.
  const [attemptsSummary, setAttemptsSummary] = useState(null);
  const [attemptsSummaryLoading, setAttemptsSummaryLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await invoicingAPI.getAutoFinalizeHistory(entityId);
      setHistory(data.runs || []);
    } catch (err) {
      // Silent fail for history
    } finally {
      setHistoryLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const runAutoFinalize = useCallback(async () => {
    setConfirmOpen(false);
    setRunning(true);
    setResult(null);
    try {
      const { data } = await invoicingAPI.autoFinalize(entityId, year, month);
      setResult(data);
      if (data.month_rejected) {
        toast.error(data.month_rejected_reason || 'Month is not eligible for finalization.');
      } else if (data.circuit_breaker_tripped) {
        toast.error('Circuit breaker tripped. No invoices were finalized.');
      } else if (data.future_baseline_skipped) {
        toast.error('Entity skipped: future-baseline constraint.');
      } else if (data.finalized_count > 0) {
        toast.success(`Finalized ${data.finalized_count} invoice${data.finalized_count > 1 ? 's' : ''}.`);
      } else {
        toast.info('No invoices to finalize.');
      }
      loadHistory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }, [entityId, year, month, loadHistory]);

  const runPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await invoicingAPI.finalizePreview(entityId, year, month);
      setPreview(data);
      if (data.candidates === 0) {
        toast.info('No generator-created drafts found for this period.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  }, [entityId, year, month]);

  const loadAttemptsSummary = useCallback(async () => {
    setAttemptsSummaryLoading(true);
    try {
      const { data } = await invoicingAPI.finalizeAttemptsSummary(entityId, year, month);
      setAttemptsSummary(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAttemptsSummaryLoading(false);
    }
  }, [entityId, year, month]);

  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const { data } = await invoicingAPI.finalizeAttemptsBackfill(entityId, year, month);
      if (data.backfilled > 0) {
        toast.success(`Backfilled ${data.backfilled} attempt${data.backfilled > 1 ? 's' : ''}.`);
      } else if (data.message) {
        toast.info(data.message);
      } else {
        toast.info('Nothing left to backfill for this period.');
      }
      await loadAttemptsSummary();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBackfilling(false);
    }
  }, [entityId, year, month, loadAttemptsSummary]);

  const stopApplyPoll = useCallback(() => {
    if (applyPollRef.current) {
      clearInterval(applyPollRef.current);
      applyPollRef.current = null;
    }
  }, []);

  useEffect(() => stopApplyPoll, [stopApplyPoll]);

  const pollApplyChainOnce = useCallback(async () => {
    try {
      const { data } = await invoicingAPI.finalizeApplyChainStatus(entityId, year, month);
      const chain = data.chain || null;
      setApplyChain(chain);
      if (!chain || chain.status !== 'running') {
        stopApplyPoll();
        setApplying(false);
        if (chain?.status === 'complete') {
          const finalized = chain.seed?.report?.finalized_count || 0;
          const cbTripped = chain.seed?.report?.circuit_breaker_tripped;
          if (cbTripped) {
            toast.error('Circuit breaker tripped. No invoices were finalized.');
          } else if (finalized > 0) {
            toast.success(`Finalized ${finalized} invoice${finalized > 1 ? 's' : ''}.`);
          } else {
            toast.info('No invoices finalized.');
          }
          loadHistory();
          // Re-Preview so the list reflects what's left (idempotent re-Apply).
          const refreshed = await invoicingAPI.finalizePreview(entityId, year, month);
          setPreview(refreshed.data);
        } else if (chain?.status === 'error') {
          toast.error(chain.error || 'Finalize-apply run failed.');
        } else if (chain?.status === 'stopped') {
          toast.info('Finalize-apply run was stopped.');
        }
      }
    } catch (err) {
      stopApplyPoll();
      setApplying(false);
      toast.error(getErrorMessage(err));
    }
  }, [entityId, year, month, loadHistory, stopApplyPoll]);

  const startApplyPoll = useCallback(() => {
    stopApplyPoll();
    pollApplyChainOnce();
    applyPollRef.current = setInterval(pollApplyChainOnce, APPLY_POLL_INTERVAL_MS);
  }, [pollApplyChainOnce, stopApplyPoll]);

  const runApply = useCallback(async () => {
    setApplyConfirmOpen(false);
    setApplying(true);
    try {
      const { data } = await invoicingAPI.finalizeApply(entityId, year, month);
      if (data.status === 'chain_unavailable') {
        setApplying(false);
        toast.error('Could not start the finalize-apply run. Try again.');
        return;
      }
      startApplyPoll();
    } catch (err) {
      setApplying(false);
      toast.error(getErrorMessage(err));
    }
  }, [entityId, year, month, startApplyPoll]);

  // On mount / period change, check whether a run is already in progress
  // for this entity/period (e.g. the operator navigated away and back
  // mid-run) so progress resumes instead of looking like nothing is
  // happening.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await invoicingAPI.finalizeApplyChainStatus(entityId, year, month);
        if (cancelled) return;
        const chain = data.chain || null;
        setApplyChain(chain);
        if (chain?.status === 'running') {
          setApplying(true);
          startApplyPoll();
        }
      } catch (err) {
        // Silent -- this is a background resume-detection check.
      }
    })();
    return () => {
      cancelled = true;
      stopApplyPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, year, month]);

  const applyReport = applyChain?.seed?.report || null;
  const applyResumeState = applyChain?.seed?._resume_state || null;
  const applyRunning = applyChain?.status === 'running';

  const applyRowStatusById = useMemo(() => {
    const map = {};
    if (!applyReport) return map;
    for (const f of applyReport.finalized || []) {
      if (f.invoice_id != null) map[f.invoice_id] = { status: 'finalized', invoice_number: f.invoice_number };
    }
    for (const g of applyReport.gate_results || []) {
      if (g.invoice_id != null && !g.gate_pass && !(g.invoice_id in map)) {
        map[g.invoice_id] = { status: 'left_draft', reasons: g.reasons };
      }
    }
    const pendingQueue = [
      ...(applyResumeState?.gate_queue || []),
      ...(applyResumeState?.finalize_queue || []),
    ];
    for (const p of pendingQueue) {
      const id = p.invoice_id != null ? p.invoice_id : p.pennylane_invoice_id;
      if (id != null && !(id in map)) map[id] = { status: 'pending' };
    }
    return map;
  }, [applyReport, applyResumeState]);

  const previewPassingCount = (preview?.rows || []).filter((r) => r.gate_pass).length;

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 1; y <= currentYear + 1; y++) years.push(y);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" />
          Auto-Finalize Pipeline
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Generate, gate-check, and finalize all draft invoices for a given month.
          This is irreversible: finalized invoices get a legal number and can only be
          reversed via credit note.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Period selector */}
        <div className="flex items-end gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {months.map((m) => (
                <option key={m} value={m} disabled={!isCompletedMonth(year, m)}>
                  {new Date(2000, m - 1).toLocaleString('en', { month: 'short' })}{!isCompletedMonth(year, m) ? ' (in progress)' : ''}
                </option>
              ))}
            </select>
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={running || !isCompletedMonth(year, month)}
            variant="default"
          >
            {running
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Play className="h-4 w-4 mr-2" />}
            Run Auto-Finalize
          </Button>
          <Button
            data-testid="finalize-preview-button"
            onClick={runPreview}
            disabled={previewLoading || !isCompletedMonth(year, month)}
            variant="outline"
          >
            {previewLoading
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Eye className="h-4 w-4 mr-2" />}
            Preview Drafts
          </Button>
        </div>

        {/* Result display */}
        {result && (
          <div className="space-y-4">
            {/* Month rejected alert */}
            {result.month_rejected && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Month not eligible
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {result.month_rejected_reason || 'The selected month cannot be finalized.'}
                </p>
              </div>
            )}

            {/* Circuit breaker alert */}
            {result.circuit_breaker_tripped && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Circuit breaker tripped ({((result.circuit_breaker_rate || 0) * 100).toFixed(0)}% failed)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  More than 20% of candidates failed gate checks. No invoices were finalized
                  as a safety measure.
                </p>
              </div>
            )}

            {/* Future baseline alert */}
            {result.future_baseline_skipped && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Future-baseline: skipped
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Latest finalized invoice is dated {result.future_baseline_date}, which is
                  after today. No invoice can be finalized until that date passes.
                </p>
              </div>
            )}

            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Candidates</p>
                <p className="text-xl font-bold tabular-nums">{result.candidates || 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Finalized</p>
                <p className="text-xl font-bold tabular-nums text-green-600">{result.finalized_count || 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Skipped</p>
                <p className="text-xl font-bold tabular-nums text-amber-600">{result.skipped_count || 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Errors</p>
                <p className="text-xl font-bold tabular-nums text-red-600">{result.error_count || 0}</p>
              </div>
            </div>

            {/* Finalized table */}
            {result.finalized?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Finalized ({result.finalized.length})
                </h4>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                        <th className="text-left px-3 py-2 font-medium">Booking</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.finalized.map((f, i) => (
                        <tr key={f.invoice_id || i} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{f.invoice_number || '?'}</td>
                          <td className="px-3 py-2 font-mono text-xs">{f.uplisting_id}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {f.amount != null ? Number(f.amount).toFixed(2) : ''}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{f.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Skipped table */}
            {result.gate_results?.filter((g) => !g.gate_pass).length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">
                  Skipped ({result.gate_results.filter((g) => !g.gate_pass).length})
                </h4>
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Booking</th>
                        <th className="text-left px-3 py-2 font-medium">Invoice ID</th>
                        <th className="text-left px-3 py-2 font-medium">Reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.gate_results.filter((g) => !g.gate_pass).map((s, i) => (
                        <tr key={s.invoice_id || i} className="border-b">
                          <td className="px-3 py-2 font-mono text-xs">{s.uplisting_id}</td>
                          <td className="px-3 py-2 font-mono text-xs">{s.invoice_id}</td>
                          <td className="px-3 py-2 text-xs">
                            {(s.reasons || []).map((r) => (
                              <Badge key={r} variant="outline" className="mr-1 text-xs">{r}</Badge>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
                <p className="text-sm font-medium flex items-center gap-1.5 mb-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Errors ({result.errors.length})
                </p>
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {e.uplisting_id || e.stage}: {e.error}
                  </p>
                ))}
              </div>
            )}

            {/* Empty result */}
            {!result.circuit_breaker_tripped && !result.future_baseline_skipped &&
             result.finalized_count === 0 && result.error_count === 0 && result.skipped_count === 0 && (
              <div className="rounded-lg border p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-sm text-muted-foreground">
                  No candidates found for this period. Nothing to finalize.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Row-level Preview/Apply: drain pre-existing generator drafts */}
        {preview && (
          <div className="space-y-3 rounded-lg border p-3" data-testid="finalize-preview-section">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                Generator drafts for {preview.period} ({preview.candidates})
              </h4>
              <div className="flex items-center gap-2">
                {!preview.flag_enabled && (
                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">
                    finalize flag off
                  </Badge>
                )}
                <Button
                  data-testid="finalize-apply-button"
                  size="sm"
                  variant="destructive"
                  onClick={() => setApplyConfirmOpen(true)}
                  disabled={applying || previewPassingCount === 0}
                >
                  {applying
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    : <Zap className="h-4 w-4 mr-1" />}
                  {applyRunning
                    ? `Finalizing… ${(applyReport?.finalized_count || 0) + (applyReport?.skipped_count || 0)}/${applyReport?.candidates || previewPassingCount}`
                    : `Apply (${previewPassingCount})`}
                </Button>
              </div>
            </div>

            {applyRunning && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="finalize-apply-progress">
                <Loader2 className="h-3 w-3 animate-spin" />
                Running in the background -- finalized {applyReport?.finalized_count || 0}, left draft {(applyReport?.gate_results || []).filter((g) => !g.gate_pass).length}. Safe to leave this page; progress resumes on return.
              </div>
            )}

            {preview.rows.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="finalize-preview-empty">
                No generator-created drafts found for this period.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium">Property</th>
                      <th className="text-left px-3 py-2 font-medium">Guest</th>
                      <th className="text-left px-3 py-2 font-medium">Channel</th>
                      <th className="text-right px-3 py-2 font-medium">Total</th>
                      <th className="text-left px-3 py-2 font-medium">Gate result</th>
                      {applyChain && <th className="text-left px-3 py-2 font-medium">Apply status</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => {
                      const applyStatus = applyChain ? applyRowStatusById[r.invoice_id] : null;
                      return (
                        <tr key={r.invoice_id || i} className="border-b">
                          <td className="px-3 py-2 text-xs">{r.property_name || '-'}</td>
                          <td className="px-3 py-2 text-xs">{r.guest_name || '-'}</td>
                          <td className="px-3 py-2 text-xs">{r.channel || '-'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.amount != null ? Number(r.amount).toFixed(2) : ''}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {r.gate_pass ? (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                                pass
                              </Badge>
                            ) : (
                              (r.reasons || []).map((reason) => (
                                <Badge key={reason} variant="outline" className="mr-1 text-xs">{reason}</Badge>
                              ))
                            )}
                          </td>
                          {applyChain && (
                            <td className="px-3 py-2 text-xs" data-testid={`finalize-apply-row-status-${r.invoice_id}`}>
                              {applyStatus?.status === 'finalized' ? (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                                  finalized{applyStatus.invoice_number ? ` #${applyStatus.invoice_number}` : ''}
                                </Badge>
                              ) : applyStatus?.status === 'left_draft' ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">
                                  left draft{applyStatus.reasons?.length ? `: ${applyStatus.reasons.join(', ')}` : ''}
                                </Badge>
                              ) : applyStatus?.status === 'pending' ? (
                                <Badge variant="outline" className="text-xs text-muted-foreground">pending</Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {applyChain && !applyRunning && applyReport && (
              <div className="text-xs text-muted-foreground border-t pt-2">
                Last Apply: finalized={applyReport.finalized_count || 0} skipped={applyReport.skipped_count || 0} errors={applyReport.error_count || 0}
              </div>
            )}
          </div>
        )}

        {/* Attempts summary: read-only per-invoice finalize_attempt counts
            for the selected period -- how many invoices a partially-
            completed run (e.g. a pre-fix 504) actually finalized before it
            was cut off. */}
        <div className="rounded-lg border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Finalize attempts for {year}-{String(month).padStart(2, '0')}</h4>
            <Button
              data-testid="finalize-attempts-summary-button"
              variant="ghost"
              size="sm"
              onClick={loadAttemptsSummary}
              disabled={attemptsSummaryLoading}
              className="h-7 text-xs"
            >
              {attemptsSummaryLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Check'}
            </Button>
          </div>
          {attemptsSummary && attemptsSummary.predates_per_invoice_logging && (
            <div className="text-xs space-y-2" data-testid="finalize-attempts-summary-result">
              <p className="text-amber-600 dark:text-amber-400">
                History predates per-invoice logging for {attemptsSummary.period}: Recent Runs
                shows {attemptsSummary.run_summary_finalized_count} finalized, but no per-invoice
                record is stamped with this period yet. This is not a real 0.
              </p>
              <Button
                data-testid="finalize-attempts-backfill-button"
                variant="outline"
                size="sm"
                onClick={runBackfill}
                disabled={backfilling}
                className="h-7 text-xs"
              >
                {backfilling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Backfill from run history
              </Button>
            </div>
          )}
          {attemptsSummary && !attemptsSummary.predates_per_invoice_logging && (
            <div className="text-xs space-y-1" data-testid="finalize-attempts-summary-result">
              <p>
                {attemptsSummary.total_attempts} attempt{attemptsSummary.total_attempts === 1 ? '' : 's'} logged,{' '}
                <span className="text-green-600 font-medium">{attemptsSummary.finalized_count} finalized</span>.
              </p>
              {Object.entries(attemptsSummary.by_outcome_reason || {}).map(([key, count]) => (
                <p key={key} className="text-muted-foreground font-mono">{key || '(no reason)'}: {count}</p>
              ))}
            </div>
          )}
        </div>

        {/* History section */}
        <div>
          <h4 className="text-sm font-medium flex items-center gap-1.5 mb-2">
            <History className="h-4 w-4" />
            Recent Runs
            <Button variant="ghost" size="sm" onClick={loadHistory} disabled={historyLoading} className="ml-auto h-7 text-xs">
              {historyLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
            </Button>
          </h4>
          {history.length === 0 && !historyLoading && (
            <p className="text-xs text-muted-foreground">No runs recorded yet.</p>
          )}
          {history.length > 0 && (
            <div className="overflow-x-auto max-h-48 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Period</th>
                    <th className="text-left px-3 py-2 font-medium">Trigger</th>
                    <th className="text-right px-3 py-2 font-medium">Finalized</th>
                    <th className="text-right px-3 py-2 font-medium">Skipped</th>
                    <th className="text-right px-3 py-2 font-medium">Errors</th>
                    <th className="text-center px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run, i) => {
                    const r = run.report || {};
                    const aborted = r.circuit_breaker_tripped;
                    const skipped = r.future_baseline_skipped;
                    return (
                      <tr key={i} className={`border-b ${aborted ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                        <td className="px-3 py-2 font-mono text-xs">{(run.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-3 py-2 text-xs">{run.period}</td>
                        <td className="px-3 py-2 text-xs">{r.trigger || '?'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.finalized_count || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.skipped_count || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.error_count || 0}</td>
                        <td className="px-3 py-2 text-center">
                          {aborted ? (
                            <Badge variant="destructive" className="text-xs">aborted</Badge>
                          ) : skipped ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">baseline</Badge>
                          ) : r.finalized_count > 0 ? (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">ok</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">empty</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Confirm dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm auto-finalize</DialogTitle>
              <DialogDescription>
                This will generate drafts (if needed), run gate checks, and finalize
                all passing invoices for {entityId} in {year}-{String(month).padStart(2, '0')}.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm space-y-2 py-2">
              <p className="text-red-600 dark:text-red-400 font-medium">
                Finalization is irreversible. Finalized invoices get a legal number
                and can only be reversed via credit note.
              </p>
              <p>
                A circuit breaker will abort if more than 20% of candidates fail
                gate checks.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={runAutoFinalize}>
                Finalize {year}-{String(month).padStart(2, '0')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply confirm dialog (row-level Preview/Apply) */}
        <Dialog open={applyConfirmOpen} onOpenChange={setApplyConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm finalize</DialogTitle>
              <DialogDescription>
                This will finalize {previewPassingCount} gate-passing draft
                {previewPassingCount === 1 ? '' : 's'} for {entityId} in {year}-{String(month).padStart(2, '0')}.
                Rows that fail their gate stay draft.
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm py-2">
              <p className="text-red-600 dark:text-red-400 font-medium">
                Finalization is irreversible. Finalized invoices get a legal number
                and can only be reversed via credit note.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={runApply}>
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
